import React, {
  ChangeEventHandler,
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import usePlacesAutocomplete, {
  GeocodeResult,
  Suggestion,
  getGeocode,
  getLatLng,
  getZipCode,
} from 'use-places-autocomplete';
import { Paper, PaperProps, Popper, PopperProps } from '@mui/material';
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { ExpandContent, ExpandInfoBoxClickable, InfoBox, InfoBoxTypes } from '../../../infobox';
import { breakpoint as breakpoints, mediaQuery, typography } from '../../../../theme';
import { AddressInputField, ApartmentNumberInputField } from './AddressInput';
import {
  trackInsightsEvent,
  trackInsightsException,
} from '../../../../utils/telemetry/appinsights';
import { UserAddressDto } from '@kotipizzagroup/kotipizza-users-api-client';
import { colors } from '../../../../theme/color';

const ErrorMessage = styled(typography.ParagraphSmall)`
  color: ${colors.orange.darker(60)};
`;

const TooManyResultsHint = styled(typography.ParagraphSmall)`
  margin-left: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

type AddressInputProps = {
  // Address input placeholder
  placeholder?: string;

  // Handler when user select address
  onAddressSelect: (address: UserAddressDto) => void;

  // Handler when user denies location

  onLocationDenied?: () => void;
  onApartmentNumberChange: ChangeEventHandler<HTMLInputElement>;

  // Preselect Address input
  preselectAddress?: string;
  preselectApartmentNumber?: string;
  breakpoint?: number;

  type?: 'primary' | 'secondary';

  // Optional functions to call when autosuggest input value changes
  onInputValueChange?: () => void;
};

const AddressSuggestion = styled.div`
  cursor: pointer;
`;

const AucompletePoweredByGoogle = styled.div`
  padding: 0 16px 6px;
  display: flex;
  justify-content: flex-end;
`;

const StyledOl = styled.ol`
  margin: 10px 0 0;
  font-size: inherit;

  li {
    &:first-child {
      margin: 16px 0;
      padding-bottom: 0;
    }
  }
`;

const getCityFromPlacesSuggestion = (structuredText: { secondary_text?: string }): string => {
  return structuredText.secondary_text?.split(',')[0] || '';
};

const addressHasStreetNumber = (address: string): boolean => {
  return /\d+/.test(address);
};

export const AddressInput: React.FC<AddressInputProps> = ({
  placeholder,
  onAddressSelect,
  onLocationDenied = () => undefined,
  preselectAddress = '',
  preselectApartmentNumber,
  onApartmentNumberChange,
  type,
  onInputValueChange,
  breakpoint = breakpoints.medium,
}) => {
  const {
    ready,
    value,
    suggestions: { status, data = [] },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ['address'],
      componentRestrictions: {
        country: 'fi',
      },
    },
    debounce: 600,
  });

  useEffect(() => {
    return () => {
      clearSuggestions(); // Cancel autoComplete debounce on unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationSupported = 'geolocation' in navigator;

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (status === 'OK') {
      setLoading(false);
    }
  }, [data, status]);

  useEffect(() => {
    if (preselectAddress) {
      setValue(preselectAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectAddress]);

  const [previousTerm, setPreviousTerm] = useState('');
  const [missingHomeNumber, setMissingHomeNumber] = useState(false);
  const [missingZipCode, setMissingZipCode] = useState(false);

  const inputRef = useRef<MutableRefObject<HTMLInputElement> | null>(null);

  const handleInput = (value: string): void => {
    clearSuggestions();
    setLoading(true);
    if (!value) {
      setPreviousTerm('');
    }
    setValue(value);
    setMissingHomeNumber(false);
    if (onInputValueChange) {
      onInputValueChange();
    }
  };

  const handleSuggestionSelect = async (suggestion: string | Suggestion): Promise<void> => {
    if (!suggestion || typeof suggestion === 'string') return;

    const {
      types,
      structured_formatting,
      terms,
      place_id,
      description: formattedAddress,
    } = suggestion;

    if (
      types.includes('street_address') ||
      types.includes('premise') ||
      types.includes('subpremise') ||
      (previousTerm === terms[0].value && addressHasStreetNumber(value)) ||
      (types.includes('route') && addressHasStreetNumber(value))
    ) {
      const streetAddress = structured_formatting.main_text; // Street address and number, e.g. (Eteläesplanadi 8)
      const city = getCityFromPlacesSuggestion(structured_formatting);
      setValue(`${streetAddress}, ${city}`, false);

      let geoCode: GeocodeResult[] = await getGeocode({
        /*
         * This has been changed to use place_id and then back to address. Problem is the autocomplete api
         * sometimes gives suggestions of the premise type which might be in the wrong location.
         * Geocode api seems to be better att guessing the correct location, so let's just give it the address for now.
         */
        address: formattedAddress,
      });

      if (
        geoCode.length > 0 &&
        (geoCode[0].partial_match || !geoCode[0].types.includes('street_address'))
      ) {
        trackInsightsEvent('FRONTEND_ADDRESS_LOOKUP_PARTIAL_MATCH', {
          formattedAddress,
          geoCode,
        });
        geoCode = await getGeocode({ placeId: place_id });
      }

      const geoCodeStreetName =
        geoCode.length > 0
          ? geoCode[0].address_components?.find((component) => {
              return component.types.includes('route');
            })?.long_name
          : null;

      const suggestionStreetNumber =
        Number(suggestion.terms[1].value) > 0 && suggestion.terms[1].value;

      const geoCodeStreetNumber =
        geoCode.length > 0
          ? geoCode[0].address_components?.find((component) => {
              return component.types.includes('street_number');
            })?.long_name
          : null;

      if (!streetAddress.includes(geoCodeStreetName || '')) {
        trackInsightsEvent('FRONTEND_ADDRESS_LOOKUP_STREET_ADDRESS_MISMATCH', {
          streetAddress,
          geoCodeStreetName,
        });
      } else if (
        `${geoCodeStreetName} ${geoCodeStreetNumber}` !== streetAddress &&
        `${geoCodeStreetName} ${suggestionStreetNumber}` !== streetAddress
      ) {
        trackInsightsEvent('FRONTEND_ADDRESS_LOOKUP_ADDRESS_MISMATCH', {
          geoCodeStreetName,
          geoCodeStreetNumber,
          streetAddress,
        });
      }

      const { lat, lng } = getLatLng(geoCode[0]);
      const postalCode = getZipCode(geoCode[0], false);

      if (!postalCode) {
        trackInsightsEvent('FRONTEND_ADDRESS_SELECTION_MISSING_POSTAL', {
          formattedAddress,
        });
        setMissingZipCode(true);
        return;
      }

      if (!geoCodeStreetNumber && !suggestionStreetNumber) {
        trackInsightsEvent('FRONTEND_ADDRESS_SELECTION_MISSING_STREETNUMBER', {
          formattedAddress,
        });
        setMissingHomeNumber(true);
        return;
      } else {
        setMissingHomeNumber(false);
      }

      clearSuggestions();
      setMissingZipCode(false);
      if (inputRef && inputRef?.current?.current) {
        inputRef.current.current.blur();
      }

      if (!missingHomeNumber) {
        onAddressSelect({
          streetAddress,
          city,
          zipCode: postalCode,
          latitude: String(lat),
          longitude: String(lng),
        } as UserAddressDto);
      }
    } else if (types.includes('route')) {
      if (inputRef?.current?.current) {
        const city = getCityFromPlacesSuggestion(structured_formatting);
        const streetName = structured_formatting.main_text;
        setValue(`${streetName} , ${city}`, false);
        inputRef?.current.current.focus();

        // without setTimeout 0 the carret is not set to correct postion
        setTimeout(() => {
          if (inputRef?.current?.current) {
            inputRef.current.current.selectionStart = inputRef.current.current.selectionEnd =
              streetName.length + 1;
          }
        }, 0);

        setMissingHomeNumber(true);
      }
    }
    setPreviousTerm(terms[0].value);
  };

  const noResultsFromGoogle = (status: string): boolean => {
    const emptyResultsCodes = ['NOT_FOUND', 'ZERO_RESULTS', 'UNKNOWN_ERROR'];
    return emptyResultsCodes.includes(status);
  };

  const renderAddressNotRecognized = (): ReactNode => {
    return (
      <InfoBox type={InfoBoxTypes.WARNING}>
        <ErrorMessage>
          <span>
            <span>
              Höh, emme tunnista osoitettasi. Huomaathan, että rappu tai asunto tulee omaan
              kenttäänsä.
            </span>
            <br />
            <ExpandInfoBoxClickable>Ei löydy vieläkään?</ExpandInfoBoxClickable>
          </span>
          <ExpandContent>
            <StyledOl>
              <span>Ei hätää, näin saat tilattua:</span>
              <li>
                <span>Kirjoita lähelläsi oleva osoite tai</span>{' '}
                <GetNearbyLocationText onClick={getUserLocation}>
                  paikanna läheinen sijainti.
                </GetNearbyLocationText>
              </li>
              <li>
                Kirjoita tilausvaiheessa lisätietokenttään oikea osoitteesi ja tarvittaessa ohjeita
                kuljettajalle.
              </li>
            </StyledOl>
          </ExpandContent>
        </ErrorMessage>
      </InfoBox>
    );
  };

  const getOptionLabel = (option: Suggestion): string => {
    const {
      structured_formatting: { main_text },
      structured_formatting,
    } = option;

    const city = getCityFromPlacesSuggestion(structured_formatting);
    return `${main_text}, ${city}`;
  };

  type AddressComponentReturn = Promise<string | null>;

  const getAddressComponent = (
    result: google.maps.GeocoderResult,
    component: string
  ): AddressComponentReturn =>
    new Promise((resolve, reject) => {
      try {
        let addressComponent: string | null = null;

        result.address_components.forEach(({ long_name, types }) => {
          if (types.includes(component)) addressComponent = long_name;
        });

        resolve(addressComponent);
      } catch (error) {
        reject(error);
      }
    });

  const getUserLocation = (): void => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const geoCodeResult = await getGeocode({
          location: { lat: latitude, lng: longitude },
        });
        const streetAddressResult = geoCodeResult.filter((x) => {
          return x.types.includes('street_address') || x.types.includes('route');
        })[0];
        const city = (await getAddressComponent(streetAddressResult, 'locality')) ?? '';
        const zipCode = (await getAddressComponent(streetAddressResult, 'postal_code')) ?? '';
        const streetName = (await getAddressComponent(streetAddressResult, 'route')) ?? '';
        const streetNumber =
          (await getAddressComponent(streetAddressResult, 'street_number')) ?? '';
        setValue(`${streetName} ${streetNumber}, ${city}`);
        onAddressSelect({
          streetAddress: `${streetName} ${streetNumber}`,
          city,
          zipCode,
          latitude: String(latitude),
          longitude: String(longitude),
        } as UserAddressDto);
        if (inputRef) {
          inputRef.current?.current?.blur();
        }
      },
      (err) => {
        trackInsightsException('FRONTEND_USER_LOCATION_DENIED', err);
        onLocationDenied();
      }
    );
  };

  const renderAddressOption = (
    value: string
  ): ((
    props: React.HTMLAttributes<HTMLLIElement>,
    suggestion: google.maps.places.AutocompletePrediction
  ) => React.ReactNode) => {
    const AddressOption = (
      props: React.HTMLAttributes<HTMLLIElement>,
      suggestion: google.maps.places.AutocompletePrediction
    ): React.ReactNode => {
      const {
        place_id,
        structured_formatting: { main_text },
        structured_formatting,
      } = suggestion;

      const city = getCityFromPlacesSuggestion(structured_formatting);
      const text = `${main_text}, ${city}`;
      const matches = match(text, value);
      const parts = parse(text, matches);

      return (
        <li {...props}>
          <AddressSuggestion key={place_id}>
            {parts.map((part, index) => (
              <typography.Text
                key={index}
                style={{
                  fontWeight: part.highlight ? 700 : 400,
                  fontSize: '1rem',
                  whiteSpace: 'pre',
                }}
              >
                {part.text}
              </typography.Text>
            ))}
          </AddressSuggestion>
        </li>
      );
    };
    return AddressOption;
  };

  const AutocompleteContainer: React.FC<PopperProps> = (props: PopperProps) => {
    return (
      <Popper {...props} placement="bottom">
        {props.children}
      </Popper>
    );
  };

  const AutocompleteContent: React.FC = (props: PaperProps) => {
    const optionsExist = !!data.length;

    const img = useMemo(
      () => (
        <img
          src="https://mob-cdn.azureedge.net/kuvat/powered_by_google_on_white.png"
          alt="Powered by google"
        />
      ),
      []
    );

    return (
      <Paper>
        {props.children}
        {data.length >= 5 && (
          <TooManyResultsHint>Useita tuloksia, tarkenna hakua</TooManyResultsHint>
        )}
        {(optionsExist || loading) && <AucompletePoweredByGoogle>{img}</AucompletePoweredByGoogle>}
      </Paper>
    );
  };

  return (
    <AddressContainer data-clarity-mask="true" breakpoint={breakpoint}>
      <div>
        <Autocomplete
          autoHighlight={true}
          autoSelect={true}
          popupIcon={null}
          disablePortal={true}
          disableClearable={true}
          loading={loading}
          loadingText="Ladataan..."
          freeSolo
          id="address-autocomplete"
          getOptionLabel={(option: Suggestion | string) =>
            typeof option === 'string' ? option : getOptionLabel(option)
          }
          options={data}
          filterOptions={(x) => x}
          isOptionEqualToValue={(option: Suggestion, value: Suggestion) => {
            return option?.place_id === value?.place_id;
          }}
          onChange={(_, newValue: string | Suggestion) => {
            handleSuggestionSelect(newValue);
          }}
          inputValue={value}
          onInputChange={(_, newInputValue: string, reason: AutocompleteInputChangeReason) => {
            if (reason === 'input') {
              handleInput(newInputValue);
            }
          }}
          renderOption={renderAddressOption(value)}
          // Material UI has typed its params wrong so...
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          renderInput={(params: any) => {
            const { ref, ...rest } = params.inputProps;

            inputRef.current = ref;

            return (
              <div ref={params.InputProps.ref}>
                <AddressInputField
                  {...rest}
                  type={type}
                  label="Kadunnimi ja -numero"
                  name="address-search" // A name or id ending in "search" will disable 1Password popups: https://1password.community/discussion/comment/606453/#Comment_606453
                  inputRef={ref}
                  placeholder={placeholder}
                  aria-describedby="address-input-error"
                  disabled={!ready}
                  onBlur={(event) => {
                    rest.onBlur(event);
                  }}
                  showLocation={
                    (noResultsFromGoogle(status) || missingZipCode) && locationSupported
                  }
                  onLocationClick={getUserLocation}
                />
              </div>
            );
          }}
          PaperComponent={AutocompleteContent}
          PopperComponent={AutocompleteContainer}
        />

        <div id="address-input-error">
          {missingHomeNumber && (
            <InfoBox type={InfoBoxTypes.WARNING}>
              <ErrorMessage>Syötä kadunnumero</ErrorMessage>
            </InfoBox>
          )}
          {(noResultsFromGoogle(status) || missingZipCode) &&
            !missingHomeNumber &&
            renderAddressNotRecognized()}
        </div>
      </div>
      <ApartmentNumberInputField
        type={type}
        label="Rappu/asunto"
        placeholder="Esim. C 24"
        onChange={onApartmentNumberChange}
        value={preselectApartmentNumber}
        maxLength={20}
      />
    </AddressContainer>
  );
};

const AddressContainer = styled.div<{ breakpoint: number }>`
  display: grid;
  grid-template-columns: calc(100% - 146px) 130px;
  gap: 16px;

  ${(p) =>
    `@media ${mediaQuery.custom(p.breakpoint)} {
      grid-template-columns: 100%;
    }`}

  .MuiAutocomplete-noOptions {
    display: none !important;
  }
  .MuiInputBase-root {
    background: white;
    fieldset {
      legend {
        display: none;
      }
    }
  }

  .MuiAutocomplete-input {
    max-width: none;
  }

  .MuiAutocomplete-paper {
    ${typography.textStyle}
  }

  .MuiPaper-root {
    border-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
    ${typography.textStyle}
  }

  & li.MuiAutocomplete-option {
    padding-top: 16px;
    padding-bottom: 16px;

    &.Mui-focused {
      background: ${colors.green.lighter(90)} !important;

      &:hover {
        background: ${colors.green.lighter(90)};
      }
    }
  }
`;

const GetNearbyLocationText = styled.span`
  color: ${colors.orange.base};
  text-decoration: underline;

  &:hover {
    cursor: pointer;
    color: ${colors.orange.darker(60)};
  }
`;
